import { AvailableVariantT } from '@lambda/apis/src/catalog-v2/types';
import { EU_STORES, ReebeloStoreT } from '@lambda/reebelo';

export const KEYBOARD_SLUG = 'keyboardLayout';

/**
 * Keyboard-layout variant values pair a layout with a language, as
 * "QWERTY - English" or "AZERTY - Français". Only the language half is
 * comparable against the store's own language.
 *
 * The separator is matched loosely because the catalogue is inconsistent about
 * which dash it uses once a value has been translated.
 */
export const getKeyboardLanguage = (label: string): string =>
  label
    .split(/\s[-–—]\s/)
    .pop()
    ?.trim() ?? '';

/** Lowercased and stripped of accents, so "Français" compares as "francais". */
const normalise = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

/**
 * Every name a store language goes by, keyed on the English name that
 * `settings.language` holds.
 *
 * The catalogue translates these values into the store's own language, but only
 * for some markets — France returns "AZERTY - Français" while Germany and Italy
 * still return "QWERTZ - German". Comparing the label against the English name
 * alone therefore only ever matched on English storefronts, so a French shopper
 * was warned that an AZERTY French keyboard might not be in their language.
 * Listing both the endonym and the other markets' names covers either case.
 *
 * Accents are stripped before comparison, so unaccented spellings are implied.
 */
const LANGUAGE_NAMES: Record<string, string[]> = {
  english: ['english', 'anglais', 'englisch', 'ingles', 'inglese'],
  french: ['french', 'francais', 'franzosisch', 'frances', 'francese'],
  german: ['german', 'deutsch', 'allemand', 'aleman', 'tedesco'],
  spanish: ['spanish', 'espanol', 'espagnol', 'spanisch', 'spagnolo'],
  italian: ['italian', 'italiano', 'italien', 'italienisch'],
};

/**
 * Whether a keyboard option is in the language the store sells in, and so worth
 * recommending to this shopper.
 *
 * Matched as a substring because layouts qualify the language rather than name
 * it plainly — "US English" and "Anglais américain" are both English. Values
 * naming a country instead of a language ("AZERTY - Belge") match nothing,
 * which is the intended outcome.
 */
export const matchesStoreLanguage = (
  label: string,
  storeLanguage: string | undefined,
): boolean => {
  if (!storeLanguage) {
    return false;
  }

  const language = normalise(getKeyboardLanguage(label));

  if (!language) {
    return false;
  }

  const store = normalise(storeLanguage);
  const names = LANGUAGE_NAMES[store] ?? [store];

  return names.some((name) => language.includes(name));
};

/**
 * Variants that keep their selector even with a single option, because they
 * report what you're buying rather than offer a choice.
 */
const ALWAYS_SHOWN_SLUGS = ['condition', 'batteryHealth'];

/**
 * Whether a variant earns a selector on the PDP.
 *
 * A one-option variant is normally hidden — there is nothing to pick. Keyboard
 * layout is the exception on EU stores (RT-17152): the layout is what a shopper
 * has to confirm matches their language, and it's what drives the mismatch
 * warning in options/Keyboard.tsx, so hiding it leaves them no way to check —
 * and no warning when the one layout in stock is the wrong one. Outside the EU
 * it stays an ordinary choice and hides like any other single option.
 *
 * Keyed on EU_STORES so a new EU market inherits this without another edit.
 */
export const isVariantShown = (
  slug: string,
  optionCount: number,
  store: ReebeloStoreT,
): boolean => {
  if (ALWAYS_SHOWN_SLUGS.includes(slug) || optionCount > 1) {
    return true;
  }

  return slug === KEYBOARD_SLUG && EU_STORES.includes(store);
};

/**
 * The SKU a PDP should open on when the URL names none.
 *
 * `availableVariants` arrives sorted price-ascending, and the default used to
 * be simply the first of them — the cheapest, layout ignored. That left a
 * store-language shopper landing on a foreign keyboard and meeting the mismatch
 * warning (RT-17152) about a choice they never made, while the layout in their
 * own language sat one click away and usually in stock.
 *
 * Language now outranks price: the cheapest SKU whose layout matches the store
 * language wins, and price only decides between those. Because the list is
 * already price-ascending, the first match is that SKU.
 *
 * Returns null when nothing matches — or the product has no keyboard layout at
 * all — leaving the caller on its cheapest-overall default.
 *
 * `displayValue ?? value` mirrors how options/Keyboard.tsx derives the label it
 * matches on, so the default agrees with the "Recommended for you" tag.
 */
export const findStoreLanguageSkuId = (
  availableVariants: AvailableVariantT[],
  storeLanguage: string | undefined,
): string | null =>
  availableVariants.find((sku) =>
    sku.variants.some(
      ({ variant, value }) =>
        variant.slug === KEYBOARD_SLUG &&
        matchesStoreLanguage(value.displayValue ?? value.value, storeLanguage),
    ),
  )?.skuId ?? null;
